import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Lock, Users, Mail, BarChart3, TrendingUp, Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-[#E8C8BE] p-4 sm:p-5" data-testid={testId}>
      <Icon size={16} className="text-[#F3B21A] mb-2" />
      <p className="font-display text-2xl sm:text-3xl font-light text-[#054452]">{value}</p>
      <p className="font-body text-[10px] sm:text-xs text-[#0C6474] mt-1">{label}</p>
    </div>
  );
}

function ArchetypeChart({ distribution, total }) {
  const sorted = useMemo(
    () => Object.entries(distribution).sort(([, a], [, b]) => b - a),
    [distribution]
  );

  return (
    <div className="bg-white border border-[#E8C8BE] p-4 sm:p-6 mb-8 sm:mb-10" data-testid="archetype-chart">
      <h3 className="font-body text-xs tracking-[0.2em] uppercase text-[#F3B21A] mb-4 flex items-center gap-2">
        <BarChart3 size={14} />
        Archetype Distribution
      </h3>
      <div className="space-y-3">
        {sorted.map(([name, count]) => {
          const pct = total > 0 ? (count / total) * 100 : 0;
          return (
            <div key={`dist-${name}`} className="flex items-center gap-3">
              <span className="font-body text-xs text-[#054452] w-20 sm:w-24 flex-shrink-0">{name}</span>
              <div className="flex-1 h-5 bg-[#F8D4CE] overflow-hidden">
                <div
                  className="h-full bg-[#054452] transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="font-body text-xs text-[#0C6474] w-16 text-right">
                {count} ({Math.round(pct)}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssessmentsTable({ assessments, page, totalPages, onPageChange }) {
  return (
    <div className="bg-white border border-[#E8C8BE] overflow-hidden" data-testid="assessments-table">
      <div className="p-4 sm:p-6 border-b border-[#E8C8BE]">
        <h3 className="font-body text-xs tracking-[0.2em] uppercase text-[#F3B21A] flex items-center gap-2">
          <Users size={14} />
          Recent Submissions
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FDF6F3]">
              <th className="font-body text-[10px] sm:text-xs text-[#0C6474] text-left px-3 sm:px-4 py-2 uppercase tracking-wider">Date</th>
              <th className="font-body text-[10px] sm:text-xs text-[#0C6474] text-left px-3 sm:px-4 py-2 uppercase tracking-wider">Primary</th>
              <th className="font-body text-[10px] sm:text-xs text-[#0C6474] text-left px-3 sm:px-4 py-2 uppercase tracking-wider">Secondary</th>
              <th className="font-body text-[10px] sm:text-xs text-[#0C6474] text-left px-3 sm:px-4 py-2 uppercase tracking-wider">Email</th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((a) => (
              <tr key={a.id} className="border-t border-[#E8C8BE] hover:bg-[#FDF6F3]">
                <td className="font-body text-[10px] sm:text-xs text-[#054452] px-3 sm:px-4 py-2.5">
                  {new Date(a.created_at).toLocaleDateString()}
                </td>
                <td className="font-body text-[10px] sm:text-xs text-[#054452] px-3 sm:px-4 py-2.5 font-medium">
                  {a.primary_archetype}
                </td>
                <td className="font-body text-[10px] sm:text-xs text-[#0C6474] px-3 sm:px-4 py-2.5">
                  {a.secondary_archetype}
                </td>
                <td className="font-body text-[10px] sm:text-xs text-[#0C6474] px-3 sm:px-4 py-2.5">
                  {a.email || "—"}
                </td>
              </tr>
            ))}
            {assessments.length === 0 && (
              <tr>
                <td colSpan={4} className="font-body text-xs text-[#0C6474] text-center py-8">
                  No assessments yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8C8BE]">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="flex items-center gap-1 font-body text-xs text-[#054452] disabled:text-[#E8C8BE]"
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <span className="font-body text-xs text-[#0C6474]">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-1 font-body text-xs text-[#054452] disabled:text-[#E8C8BE]"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function LoginForm({ password, setPassword, onSubmit, error }) {
  return (
    <div className="min-h-screen bg-[#054452] flex items-center justify-center px-6">
      <div className="max-w-[360px] w-full text-center">
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-full bg-[#035162] flex items-center justify-center">
            <Lock size={20} className="text-[#F3B21A]" />
          </div>
        </div>
        <h1 className="font-display text-2xl text-white mb-2">Admin Panel</h1>
        <p className="font-body text-xs text-[#0C6474] mb-8">Identity Analyzer Dashboard</p>

        <form onSubmit={onSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            data-testid="admin-password-input"
            className="w-full bg-[#035162] border border-[#0C6474] text-white font-body text-sm px-4 py-3 placeholder:text-[#0C6474] focus:outline-none focus:border-[#F3B21A] transition-colors mb-3"
          />
          {error && (
            <p className="font-body text-xs text-[#E66E6D] mb-3">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="w-full bg-[#F3B21A] text-[#054452] py-3 font-body text-sm font-medium uppercase tracking-wide hover:bg-[#E66E6D] hover:text-white transition-colors"
          >
            Access Dashboard
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminPanel() {
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const [stats, setStats] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setAuthenticated(true);
      setAuthError("");
    } catch {
      setAuthError("Invalid password");
    }
  }, [password]);

  const fetchData = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const [statsRes, assessRes] = await Promise.all([
        axios.get(`${API}/admin/stats?password=${encodeURIComponent(password)}`),
        axios.get(`${API}/admin/assessments?password=${encodeURIComponent(password)}&limit=${pageSize}&skip=${page * pageSize}`),
      ]);
      setStats(statsRes.data);
      setAssessments(assessRes.data.assessments);
      setTotal(assessRes.data.total);
    } catch (err) {
      console.error("Failed to fetch admin data:", err);
    }
    setLoading(false);
  }, [authenticated, password, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = useCallback((newPage) => {
    setPage(Math.max(0, newPage));
  }, []);

  if (!authenticated) {
    return (
      <LoginForm
        password={password}
        setPassword={setPassword}
        onSubmit={handleLogin}
        error={authError}
      />
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen bg-[#FDF6F3]" data-testid="admin-dashboard">
      <div className="bg-[#054452] px-4 sm:px-6 py-4 sm:py-6">
        <div className="max-w-[960px] mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-display text-xl sm:text-2xl text-white">Analytics Dashboard</h1>
            <p className="font-body text-xs text-[#0C6474]">Identity Analyzer</p>
          </div>
          <button
            onClick={() => { window.location.href = "/"; }}
            className="font-body text-xs text-[#F8D4CE] border border-[#0C6474] px-4 py-2 hover:border-[#F3B21A] transition-colors"
          >
            Back to App
          </button>
        </div>
      </div>

      <div className="max-w-[960px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {loading && !stats ? (
          <p className="text-center text-[#0C6474] font-body text-sm">Loading...</p>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
              <StatCard icon={Users} label="Total Assessments" value={stats.total_assessments} testId="stat-total" />
              <StatCard icon={Mail} label="Emails Captured" value={stats.emails_captured} testId="stat-emails" />
              <StatCard icon={TrendingUp} label="Capture Rate" value={`${stats.email_capture_rate}%`} testId="stat-rate" />
              <StatCard icon={Calendar} label="Last 7 Days" value={stats.recent_7_days} testId="stat-recent" />
            </div>

            <ArchetypeChart
              distribution={stats.archetype_distribution}
              total={stats.total_assessments}
            />

            <AssessmentsTable
              assessments={assessments}
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
